<template>
  <section class="slot-inspector-summary" :class="breakpointModifiers">
    <div class="slot-inspector-summary__range">
      <h3 ref="rangeHeading" class="slot-inspector-summary__range-label" tabindex="-1">{{ rangeLabel }}</h3>
      <sp-tooltip class="slot-inspector-summary__info" :content="t('preview_only')" position="right" theme="dark">
        <sp-icon class="slot-inspector-summary__info-icon" name="info-filled" size="x-small"></sp-icon>
        <span class="slot-inspector-summary__visually-hidden">{{ t("preview_info_label") }}</span>
      </sp-tooltip>
    </div>

    <p class="slot-inspector-summary__heading">{{ t("totals_heading") }}</p>

    <ul class="slot-inspector-summary__totals">
      <li v-for="card in cards" :key="card.key" class="slot-inspector-summary__total">
        <span class="slot-inspector-summary__count">{{ card.count }}</span>
        <span class="slot-inspector-summary__total-label">{{ card.label }}</span>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { t } from "#ce/apps/SpAdmin/SpSlotInspectorApp/i18n";
import { ACTIONS } from "#ce/apps/SpAdmin/SpSlotInspectorApp/actions";
import { useBreakpoints } from "#ce/composables/useBreakpoints";

const props = defineProps({
  /**
   * The dates the preview covers, as the server words them: "Sep 9 to Oct 8, 2026".
   */
  rangeLabel: {
    type: String,
    default: "",
  },
  /**
   * Slots per action across the whole preview.
   */
  totals: {
    type: Object,
    default: () => ({}),
  },
});

const rangeHeading = useTemplateRef("rangeHeading");
const { breakpointModifiers } = useBreakpoints();

const cards = computed(() =>
  ACTIONS.map((action) => ({
    key: action.key,
    count: props.totals[action.key] ?? 0,
    label: t(`total_${action.copy}`),
  })),
);

function focusHeading() {
  rangeHeading.value?.focus();
}

defineExpose({ focusHeading });
</script>

<style scoped lang="scss">
.slot-inspector-summary {
  display: grid;
  gap: var(--sp-ref-scale-12);

  &__range {
    display: flex;
    align-items: center;
    gap: var(--sp-ref-scale-8);
    margin-block-end: var(--sp-ref-scale-12);
  }

  &__range-label {
    margin: 0;
    font-size: var(--sp-sys-typescale-body-font-size);
    font-weight: var(--sp-sys-font-weight-medium);
    color: var(--sp-sys-color-on-surface);

    // Focused by script to land the user on the results. It is not a control, so it draws no ring.
    &:focus {
      outline: none;
    }
  }

  &__info {
    --sp-comp-icon-fill: var(--sp-sys-color-neutral-secondary);
  }

  &__info-icon {
    display: block;
  }

  &__visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  &__heading {
    margin: 0;
    font-size: var(--sp-sys-typescale-small-font-size);
    font-weight: var(--sp-sys-font-weight-medium);
    color: var(--sp-sys-color-on-surface);
  }

  &__totals {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--sp-ref-scale-12);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &.--is-sm-screen &__totals {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  &__total {
    display: grid;
    justify-items: center;
    gap: var(--sp-ref-scale-4);
    padding: var(--sp-ref-scale-20) var(--sp-ref-scale-8);
    border-radius: var(--sp-ref-border-radius-sm);
    background-color: var(--sp-sys-color-surface-variant);
    color: var(--sp-sys-color-on-surface);
  }

  &__count {
    font-size: var(--sp-ref-font-size-semi-x-large);
    font-weight: var(--sp-sys-font-weight-semi-bold);
    line-height: var(--sp-ref-line-height-small);
  }

  &__total-label {
    font-size: var(--sp-sys-typescale-small-font-size);
    font-weight: var(--sp-sys-font-weight-medium);
  }
}
</style>
